import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

const COUNT_LABELS = [
  ["added", "Added"],
  ["updated", "Updated"],
  ["unchanged", "Unchanged"],
  ["rejected", "Rejected"],
  ["notProcessed", "Not processed"],
];

const ImportResultDialog = ({ open, result, onClose }) => {
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    setExpanded({});
  }, [result]);
  if (!result) return null;

  const importLabel = result.importType === "Floor" ? "Floor plan" : "Unit";
  const propertyLabel = result.propertyName || (result.propertyId ? `ID ${result.propertyId}` : "Unavailable");
  const counts = result.counts || {};
  const entities = result.entities || {};
  const rows = Array.isArray(result.rows) ? result.rows : [];

  const toggleRow = (key) => {
    setExpanded((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="import-result-title"
      aria-describedby="import-result-summary"
    >
      <DialogTitle id="import-result-title" sx={{ fontFamily: "OpenSans-Bold", fontSize: "20px", color: "#303030" }}>
        Import result
      </DialogTitle>
      <DialogContent sx={{ color: "#303030" }}>
        <p id="import-result-summary" style={{ marginTop: 0, lineHeight: 1.5 }}>
          {result.message}
        </p>
        <dl style={{ display: "grid", gridTemplateColumns: "8.5rem 1fr", gap: "6px 12px", margin: "0 0 16px" }}>
          <dt>File</dt>
          <dd style={{ margin: 0 }}>{result.fileName || "Unavailable"}</dd>
          <dt>Property</dt>
          <dd style={{ margin: 0 }}>{propertyLabel}</dd>
          <dt>Import type</dt>
          <dd style={{ margin: 0 }}>{importLabel}</dd>
          <dt>Result</dt>
          <dd style={{ margin: 0 }}>{result.overallResult}</dd>
          <dt>Data rows</dt>
          <dd style={{ margin: 0 }}>{result.totalDataRows ?? 0}</dd>
        </dl>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {COUNT_LABELS.map(([key, label]) => (
            <div key={key} style={{ border: "1px solid #D9D9D9", borderRadius: "4px", minWidth: "7.5rem", padding: "8px 12px" }}>
              <div style={{ fontSize: "12px" }}>{label}</div>
              <div style={{ fontFamily: "OpenSans-Bold", fontSize: "18px" }}>{counts[key] ?? 0}</div>
            </div>
          ))}
        </div>
        <p style={{ marginTop: 0 }}>
          Floor plans created: {entities.floorPlansCreated ?? 0}. Units created: {entities.unitsCreated ?? 0}.
        </p>
        <p style={{ marginTop: 0 }}>{result.deletionNote}</p>
        {rows.length > 0 && (
          <div>
            <h3 style={{ fontSize: "16px", marginBottom: "8px" }}>Row details</h3>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {rows.map((row, index) => {
                const key = `${row.rowNumber ?? "row"}-${index}`;
                const isOpen = Boolean(expanded[key]);
                const changes = Array.isArray(row.changes) ? row.changes : [];
                return (
                  <li key={key} style={{ borderTop: "1px solid #E6E6E6" }}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={`import-row-${key}`}
                      onClick={() => toggleRow(key)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        background: "transparent",
                        border: 0,
                        padding: "10px 0",
                        cursor: "pointer",
                        font: "inherit",
                        color: "inherit",
                      }}
                    >
                      Row {row.rowNumber ?? "—"}
                      {row.floorCode ? ` · ${row.floorCode}` : ""}
                      {row.unitName ? ` · Unit ${row.unitName}` : ""}
                      {` · ${row.outcome}`}
                    </button>
                    {isOpen && (
                      <div id={`import-row-${key}`} style={{ padding: "0 0 12px 4px" }}>
                        <div>{row.reason}</div>
                        {changes.length > 0 && (
                          <ul>
                            {changes.map((change) => (
                              <li key={change.field}>
                                {change.field}: {change.previousValue} → {change.savedValue}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose} variant="contained" autoFocus>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportResultDialog;
